/**
 * B2B landing-page configuration for med spa owners and licensed providers.
 * Each page renders via BusinessPage.jsx at its dedicated route.
 *
 * All language is compliance-aware: PeptidesForMedSpas is an educational
 * catalog. We do not prescribe, dispense, or operate as a pharmacy. Clinical
 * decisions stay with licensed clinicians and licensed pharmacy partners.
 */

const BASE = "https://www.peptidesformedspas.org";

export const BUSINESS_PAGES = {
  "for-med-spas": {
    slug: "for-med-spas",
    audience: "owner",
    breadcrumb: "For Med Spa Owners",
    eyebrow: "For Med Spa Owners",
    h1: "Add Peptide Research Education to Your Med Spa — The Right Way",
    title: "For Med Spas | Compliance-First Peptide Playbook",
    description:
      "How licensed med spa owners evaluate peptide research categories, vet medical direction, choose pharmacy partners, and structure compliant offerings. Education-first, compliance-aware. Not a dispensing or prescribing service.",
    keywords: [
      "add peptide research education to med spa", "peptide research education for med spa",
      "med spa peptide program", "how to offer peptide education at my med spa",
      "peptide research education for med spa owners", "med spa peptide education",
      "research peptides wholesale med spa", "b2b peptide research education",
    ],
    intro:
      "Patients increasingly ask about peptides before they even set foot in your med spa. This page is a clinician-respectful, compliance-first starting point for owners who want to evaluate whether and how peptide-related offerings fit their practice — without overpromising, overstepping, or underestimating regulatory risk.",
    sections: [
      {
        heading: "What your patients are actually asking about",
        body:
          "GHK-Cu and NAD+ for anti-aging, BPC-157 and TB-500 for recovery, Sermorelin and MOTS-c for hormone support, PT-141 for sexual wellness, and (most intensely) GLP-1-class medications for weight management. The first four categories are typically research peptides; GLP-1s are prescription pharmaceuticals. Mixing those categories in a single conversation is where compliance goes wrong.",
      },
      {
        heading: "The compliance stack you need",
        body:
          "A named, genuinely involved medical director. Written delegation protocols for every service. Patient intake + evaluation documented before any product touches a patient. Licensed pharmacy partners for compounded or prescription products. No mixing of research-only compounds into clinical administration. Marketing that avoids outcome guarantees, disease claims, or 'FDA-approved' language on anything that isn't.",
      },
      {
        heading: "Where PeptidesForMedSpas fits",
        body:
          "We are an educational catalog and research-peptide storefront. We curate compound-level education, link out to licensed pharmacy and dispensing partners for prescription workflows, and provide long-form evidence-based content your team can cite. We are not your medical director, your pharmacy, or your compliance attorney — and anyone who tries to be all three at once should raise a flag.",
      },
      {
        heading: "A realistic rollout sequence",
        body:
          "Start with education — patient handouts, staff training, and evidence-based wall materials for the categories you intend to discuss. Add licensed-pharmacy relationships for anything prescription or compounded. Pilot 1-2 categories with clear intake protocols before expanding. Measure complaints, adverse events, and reauthorization rates — not just top-line revenue.",
      },
    ],
    internalLinks: [
      { label: "Compliance & Regulatory Framework", to: "/compliance" },
      { label: "Provider Access — Institutional Pricing (Alpha BioMed Labs)", to: "/provider-access" },
      { label: "Partners — AlphaSync Rx & MyDose.ai (licensed clinician platforms)", to: "/partners" },
      { label: "Affiliate Program", to: "/affiliate-program" },
      { label: "Peptides for Med Spas — Research Hub", to: "/peptides-for-med-spas" },
      { label: "Med Spa Peptide Marketing Compliance Guide", to: "/blog/med-spa-peptide-marketing-compliance-guide" },
      { label: "State-by-State Regulatory Context", to: "/locations" },
      { label: "Peptide Protocols Reference (Aesthetic Practices)", to: "/peptide-protocols-for-aesthetic-practices" },
    ],
    faqs: [
      {
        q: "Can my med spa sell research peptides directly to patients?",
        a: "Research peptides are sold for laboratory use only and are not approved by the FDA for human use. Mixing research-only compounds into clinical administration creates substantial regulatory risk. Clinical use of any peptide belongs in a prescription-and-pharmacy pathway with a licensed clinician — not a research-chemical purchase on the patient's behalf.",
      },
      {
        q: "Do I need a medical director to offer peptide education at my med spa?",
        a: "Offering patient education itself does not require a medical director. Offering peptide-related clinical services — intake, evaluation, prescription, administration — does. Requirements vary by state; see your state's regulatory context under /locations and consult local counsel.",
      },
      {
        q: "What's the safest way to start?",
        a: "Education first: train staff on research-only vs. prescription language, build patient handouts, choose 1-2 categories (often anti-aging and recovery) to develop first, and route any clinical pathway through a licensed pharmacy partner with genuine clinician oversight.",
      },
      {
        q: "Can PeptidesForMedSpas compound or ship prescription products to my practice?",
        a: "No. We are not a pharmacy. We do not compound, dispense, or ship prescription products. Our catalog is research-only, and our partner pages point to licensed dispensing-infrastructure platforms (AlphaSync Rx, MyDose.ai) that clinicians can evaluate directly.",
      },
    ],
    cta: {
      title: "Take the next step",
      body: "Explore institutional pricing for research-grade peptides (Alpha BioMed Labs) or licensed-clinician dispensing infrastructure (AlphaSync Rx, MyDose.ai).",
      primaryLabel: "Provider Access & Pricing",
      primaryTo: "/provider-access",
      secondaryLabel: "View Partner Platforms",
      secondaryTo: "/partners",
    },
  },

  "wholesale-peptides-for-clinics": {
    slug: "wholesale-peptides-for-clinics",
    audience: "owner",
    breadcrumb: "Wholesale Research Peptides",
    eyebrow: "Institutional Research Sourcing",
    h1: "Wholesale Research Peptides for Med Spas, Clinics & Research Labs",
    title: "Wholesale Peptides for Clinics | Institutional Pricing",
    description:
      "Institutional-volume research-grade peptide sourcing for med spas, longevity clinics, telehealth providers, and research labs via our verified-provider partner Alpha BioMed Labs. Research-only — not for clinical administration.",
    keywords: [
      "wholesale peptides", "wholesale research peptides",
      "bulk peptides for clinics", "institutional peptide pricing",
      "med spa peptide sourcing", "research lab peptide supplier",
      "verified provider peptides", "peptide supplier for clinics",
    ],
    intro:
      "Institutional-volume buyers — research labs, licensed med spas studying compound-level education, longevity clinics, and telehealth platforms — often need verified-provider pricing on research-grade peptides with transparent Certificates of Analysis. We route qualified buyers to our verified-provider partner, Alpha BioMed Labs.",
    sections: [
      {
        heading: "What verified-provider pricing means",
        body:
          "Multi-unit pricing tiers, transparent COA (identity via MS, purity via HPLC) on every batch, documented supply chain, and account credentials that verify institutional buyer status. This is not a consumer discount — it is an institutional account structure for qualified research and clinical-education use.",
      },
      {
        heading: "Who qualifies",
        body:
          "Licensed med spas under genuine medical direction, longevity and functional-medicine clinics, telehealth platforms with licensed prescriber networks, university and private research labs, and gyms or wellness facilities operating clinician-supervised programs. Applications are reviewed by our partner for institutional qualification.",
      },
      {
        heading: "What this is not",
        body:
          "Not a prescription or compounding service. Not a license to administer research chemicals to human patients. Not a compliance blanket — institutional buyers are responsible for their own state, federal, and professional-board compliance. Research peptides remain 'for laboratory use only.'",
      },
      {
        heading: "The prescription pathway is separate",
        body:
          "For prescription or compounded products (e.g., FDA-approved GLP-1 medications, compounded peptides dispensed by licensed pharmacies under physician order), our Partners page (AlphaSync Rx, MyDose.ai) describes licensed dispensing-infrastructure platforms. These are operated for licensed clinicians, not for research-chemical buyers.",
      },
    ],
    internalLinks: [
      { label: "Apply for Provider Access (Alpha BioMed Labs)", to: "/provider-access" },
      { label: "Partners — Licensed Clinician Platforms", to: "/partners" },
      { label: "Compliance & Regulatory Framework", to: "/compliance" },
      { label: "For Med Spa Owners — Program Playbook", to: "/for-med-spas" },
      { label: "For Licensed Providers", to: "/for-providers" },
      { label: "Full Research Catalog", to: "/shop" },
    ],
    faqs: [
      {
        q: "Is a 'wholesale' peptide order the same as a prescription?",
        a: "No. Wholesale research peptides are sold for laboratory research use only. They are not prescription products, are not approved for human use, and must not be administered to patients as if they were prescription medications.",
      },
      {
        q: "Can my clinic buy compounded peptides wholesale?",
        a: "No. Compounded medications are prepared by licensed compounding pharmacies under specific FDA rules and must be dispensed against a valid clinician order for a specific patient. That is a pharmacy relationship, not a wholesale purchase.",
      },
      {
        q: "Do you provide a Certificate of Analysis (COA)?",
        a: "Yes — our verified-provider partner makes COAs available for batches on request. COAs document identity (typically via mass spectrometry) and purity (typically via HPLC).",
      },
      {
        q: "What volumes qualify for institutional pricing?",
        a: "Tiering is handled by our partner at application review. Qualifying categories include licensed med spas, longevity clinics, telehealth platforms, research labs, and clinician-supervised wellness facilities.",
      },
    ],
    cta: {
      title: "Apply for verified-provider access",
      body: "Submit your institutional details through our Provider Access page to begin verification with Alpha BioMed Labs.",
      primaryLabel: "Apply for Provider Access",
      primaryTo: "/provider-access",
      secondaryLabel: "Licensed Clinician Dispensing",
      secondaryTo: "/partners",
    },
  },

  "peptide-protocols-for-aesthetic-practices": {
    slug: "peptide-protocols-for-aesthetic-practices",
    audience: "owner",
    breadcrumb: "Peptide Protocols Reference",
    eyebrow: "Research Reference — Aesthetic Practices",
    h1: "Peptide Protocols Reference for Aesthetic Practices (Research-Only)",
    title: "Peptide Protocols | Aesthetic-Practice Reference",
    description:
      "Research-literature overviews of the peptide categories most often referenced in aesthetic-practice education — GHK-Cu, Glutathione, NAD+, BPC-157, TB-500, Sermorelin, MOTS-c, PT-141. Not clinical protocols; not medical advice.",
    keywords: [
      "peptide protocols", "peptide protocols for med spa",
      "aesthetic peptide protocols", "peptide research protocols",
      "peptide clinical reference", "peptide education for clinics",
    ],
    intro:
      "This page summarizes what published research actually examines for the peptides most often discussed in aesthetic-practice education. It is a research-literature reference, not a clinical protocol document. Real dosing, administration, monitoring, and patient selection decisions belong to a licensed clinician — not a web page.",
    sections: [
      {
        heading: "Anti-aging & skin category (GHK-Cu, Glutathione, NAD+)",
        body:
          "Dermatology research on GHK-Cu examines collagen and elastin gene expression, skin-barrier biology, and antioxidant pathways in cell-culture and animal models. Glutathione research centers on oxidative-stress biology and pigment pathways; bioavailability across oral, topical, and IV routes is formulation-dependent. NAD+ research targets mitochondrial energy and DNA repair via precursors (NR, NMN) and direct administration.",
      },
      {
        heading: "Recovery category (BPC-157, TB-500, PDA)",
        body:
          "BPC-157 is studied in animal tendon, muscle, ligament, and gut models. TB-500 (Thymosin Beta-4 fragment) is studied in wound-healing, cardiac-tissue, and inflammatory models and carries anti-doping considerations. Pentadeca Arginate (PDA) is a 15-amino-acid arginate-salt analog with limited public human data. All three are research chemicals.",
      },
      {
        heading: "Hormone & longevity category (Sermorelin, MOTS-c, α-Klotho)",
        body:
          "Sermorelin is a GHRH 1-29 analog with a long clinical history in endocrine research; any GH-axis work warrants IGF-1, glucose, and clinical monitoring under endocrinologist oversight. MOTS-c is a mitochondrial-derived peptide studied in metabolism research. Anti-α-Klotho mAb is a research antibody — a laboratory reagent for Western blot, IHC, and ELISA, not a therapeutic.",
      },
      {
        heading: "Sexual wellness category (PT-141 / Bremelanotide)",
        body:
          "PT-141 / bremelanotide is a melanocortin receptor agonist studied at MC3R and MC4R. A regulated bremelanotide pharmaceutical is FDA-approved for a specific clinical indication in adult premenopausal women; research-only 'PT-141' sold online is a different product class entirely.",
      },
    ],
    internalLinks: [
      { label: "Anti-Aging Peptides Pillar", to: "/anti-aging-peptides" },
      { label: "Recovery Peptides Pillar", to: "/recovery-peptides" },
      { label: "Hormone Peptides Pillar", to: "/hormone-peptides" },
      { label: "Sexual Wellness Peptides Pillar", to: "/sexual-wellness-peptides" },
      { label: "Peptide Glossary", to: "/peptide-glossary" },
      { label: "How to Read a Peptide Study", to: "/blog/how-to-read-peptide-study" },
      { label: "Peptide Dosing Calculator (DoseMyPeptides.com ↗)", to: "https://dosingmypeptides.com/", external: true },
      { label: "For Med Spa Owners", to: "/for-med-spas" },
      { label: "For Licensed Providers", to: "/for-providers" },
    ],
    faqs: [
      {
        q: "Are these clinical protocols?",
        a: "No. Everything on this page is a research-literature summary. Clinical administration protocols are the responsibility of a licensed clinician familiar with the patient, the compound's regulatory status, and the relevant monitoring requirements.",
      },
      {
        q: "Where do I get actual dosing guidance?",
        a: "From a licensed clinician — typically a physician, NP, or PA — who has evaluated the patient and the compound's current regulatory status. Compound-specific research-literature dosing conventions (e.g., what studies have examined) are available in individual product deep-dives; those are not clinical dosing recommendations.",
      },
      {
        q: "What about GLP-1 medications?",
        a: "FDA-approved GLP-1 receptor agonists are prescription pharmaceuticals with specific clinical trial data, monitoring requirements, and adverse-event profiles. They sit in a different product class than research peptides. See /weight-loss-peptides for regulatory context; discuss prescribing with a licensed clinician.",
      },
      {
        q: "Can I print this page for staff training?",
        a: "Yes — we encourage staff education around research-only framing and compound-level evidence. Be careful not to repurpose research-literature summaries as clinical protocols; the former is education, the latter is the practice of medicine.",
      },
    ],
    cta: {
      title: "Build your program on research-first education",
      body: "Start with the categories you intend to discuss, source research-grade materials through verified-provider channels, and keep clinical pathways in licensed hands.",
      primaryLabel: "Provider Access & Pricing",
      primaryTo: "/provider-access",
      secondaryLabel: "Compliance Framework",
      secondaryTo: "/compliance",
    },
  },

  "for-providers": {
    slug: "for-providers",
    audience: "provider",
    breadcrumb: "For Licensed Providers",
    eyebrow: "For Licensed NP, PA, MD, DO",
    h1: "Peptide Research Education for Licensed Providers (NP, PA, MD, DO)",
    title: "For Licensed Providers | Peptide Research Education",
    description:
      "Evidence-forward peptide research education for licensed NPs, PAs, MDs, and DOs working in med spa, aesthetic, longevity, and wellness practice settings. Compound-level summaries, evidence tiers, and compliance framing. Not medical advice.",
    keywords: [
      "peptide education for providers", "peptide education for NPs",
      "peptide research for physicians", "peptide clinician education",
      "peptide for nurse practitioner", "peptide for PA",
      "peptide education medical director",
    ],
    intro:
      "Licensed providers — NPs, PAs, physicians, and DOs — practicing in med spa, aesthetic, or longevity-medicine settings increasingly encounter peptide questions from patients and colleagues. This page is a provider-facing research-education entry point. It respects your clinical training: compound-level evidence, regulatory status, and honest framing where data is thin.",
    sections: [
      {
        heading: "Evidence tiers you can actually use",
        body:
          "Tier 1: FDA-approved pharmaceuticals with RCT data (GLP-1 receptor agonists for their approved indications; bremelanotide for its approved indication). Tier 2: Compounded or off-label use of approved products, managed through licensed pharmacy relationships with documented clinical rationale. Tier 3: Research peptides — mechanistic and animal-model data, limited-to-no human RCT data; laboratory-use-only labeling; not appropriate for clinical administration.",
      },
      {
        heading: "Regulatory literacy",
        body:
          "FD&C Act and state practice acts define who may prescribe, compound, and administer. DSHEA draws a line between dietary supplements and drugs. 503A and 503B govern compounding pathways; 503B outsourcing facilities are distinct from 503A pharmacies. USP <797> and <800> apply to sterile and hazardous compounding. FTC rules govern advertising. HIPAA governs PHI. Your state medical, nursing, or osteopathic board defines delegation.",
      },
      {
        heading: "Where PeptidesForMedSpas fits into your practice",
        body:
          "We are an educational catalog. Providers use our compound-level deep-dives, research summaries, and patient-facing handout-quality articles as a starting point for patient conversations. We are not a pharmacy, we do not prescribe, and we are not part of your clinical documentation chain. Our Partners page describes platforms (AlphaSync Rx, MyDose.ai) built for licensed clinicians; our Provider Access page describes verified-provider research-peptide pricing (Alpha BioMed Labs).",
      },
      {
        heading: "What to tell patients who ask about peptides",
        body:
          "Start with the category (aesthetic? recovery? hormone? weight-loss? sexual wellness?). Then the regulatory class (FDA-approved drug? compounded product? research-only compound?). Then the evidence level (Tier 1/2/3 above). Then the monitoring required for the class. Avoid mixing regulatory classes into a single recommendation; patients frequently do so on their own and the clarifying conversation is part of your value.",
      },
    ],
    internalLinks: [
      { label: "Peptide Glossary (Provider Reference)", to: "/peptide-glossary" },
      { label: "Peptide Protocols — Research Reference", to: "/peptide-protocols-for-aesthetic-practices" },
      { label: "How to Read a Peptide Study", to: "/blog/how-to-read-peptide-study" },
      { label: "GLP-1 vs Research Compounds (2026)", to: "/blog/glp-1-vs-research-compounds-2026" },
      { label: "PT-141 vs Bremelanotide: Approved Drug vs Research Peptide", to: "/blog/pt-141-vs-bremelanotide-fda-approved-vs-research" },
      { label: "Licensed Clinician Dispensing Platforms (Partners)", to: "/partners" },
      { label: "Compliance & Regulatory Framework", to: "/compliance" },
      { label: "Questions Patients Ask — Reference", to: "/blog/questions-ask-clinician-peptides" },
    ],
    faqs: [
      {
        q: "Do you sell prescription or compounded peptides to licensed providers?",
        a: "No. PeptidesForMedSpas is not a pharmacy and does not sell prescription or compounded products. Our catalog is research-grade peptides labeled for laboratory use only. Licensed clinicians who want prescription or compounding pathways should evaluate our partner platforms directly (AlphaSync Rx, MyDose.ai).",
      },
      {
        q: "Can I use research peptides clinically if I am a licensed prescriber?",
        a: "Research peptides are not approved by the FDA for human use. Administering them to patients as if they were prescription medications creates regulatory exposure regardless of prescriber license. Clinical pathways run through pharmacies and approved or compounded products — not research-chemical channels.",
      },
      {
        q: "Is your content suitable for my CME or practice documentation?",
        a: "Our content is editorial and educational, not accredited CME. It can support patient education and staff training conversations. For accredited education, use AMA/AANP/AAPA/AOA channels.",
      },
      {
        q: "What partners do you recommend for clinical workflows?",
        a: "Our Partners page describes AlphaSync Rx (dispensing infrastructure linking physicians with licensed pharmacies) and MyDose.ai (peptide-therapy management platform for licensed med spas, clinics, and telehealth). We do not endorse; licensed clinicians should evaluate platforms for their own practice model.",
      },
    ],
    cta: {
      title: "Continue with clinician-focused resources",
      body: "Use our compound-level pillar pages, provider-appropriate blog, and Partners page to evaluate how peptide-related research education fits your practice.",
      primaryLabel: "Peptide Glossary",
      primaryTo: "/peptide-glossary",
      secondaryLabel: "Clinician Dispensing Platforms",
      secondaryTo: "/partners",
    },
  },
};

export const BUSINESS_SLUGS = Object.keys(BUSINESS_PAGES);

export function buildBusinessJsonLd(page) {
  const pageUrl = `${BASE}/${page.slug}`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: page.title,
      description: page.description,
      inLanguage: "en-US",
      isPartOf: { "@id": `${BASE}/#website` },
      about: { "@id": `${BASE}/#organization` },
      breadcrumb: { "@id": `${pageUrl}#breadcrumb` },
      audience: {
        "@type": "BusinessAudience",
        audienceType: page.audience === "provider"
          ? "Licensed medical providers (NP, PA, MD, DO)"
          : "Med spa owners, longevity clinic operators, aesthetic practice administrators",
      },
      speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", ".business-intro"] },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: page.breadcrumb, item: pageUrl },
      ],
    },
    page.faqs && page.faqs.length > 0 && {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${pageUrl}#faq`,
      mainEntity: page.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ].filter(Boolean);
}
